/**
 * The mock-interview hand-off, as the sign-up page reads it.
 *
 * A reader who finishes the practice interview on a guide page and taps "Start this project, free"
 * lands here with the campaign tag, the guide, the pack and the externship already in the URL (see
 * `interviewSignUpHref`). Their SCORECARD did not travel on its own: the interview runs inside the
 * guide page's frame, whose storage the browser partitions away from this origin. Two short
 * parameters carry it now: the three round results as letters, and the company the interview named.
 *
 * 🔴 NOTHING PERSONAL GOES IN THESE PARAMETERS. Three letters and a public company name only.
 *
 * 🔴 THE SWITCH IS THE CAMPAIGN TAG IN THE URL, NEVER THE PERSISTED ONE, exactly as for Labs
 * (`labs-arrival.ts`): only a URL that names the campaign AND carries a result gets the rail, so a
 * plain visit a week later sees sign-up as it ships.
 *
 * Deliberately light (no packs, no React): the layout switcher imports it on every sign-up load.
 */
import { prefilledEmail } from '@/app/(auth)/_utils/prefilled-email';

export const INTERVIEW_ARRIVAL_CAMPAIGN = 'labs_interview';

export const INTERVIEW_ARRIVAL_PARAMS = {
  company: 'iv_co',
  rounds: 'iv_r',
} as const;

/** The interview's own `Level`, restated here so this module stays free of the interview engine. */
export type InterviewRoundLevel = 'ready' | 'shaky' | 'missing';

export const INTERVIEW_PACK_KEYS = ['consulting', 'banking', 'marketing', 'data'] as const;
export type InterviewArrivalPack = (typeof INTERVIEW_PACK_KEYS)[number];

const LEVEL_CODE: Record<InterviewRoundLevel, string> = { missing: 'm', ready: 'r', shaky: 's' };
const CODE_LEVEL: Record<string, InterviewRoundLevel> = { m: 'missing', r: 'ready', s: 'shaky' };
const ROUNDS_RE = /^[rsm]{3}$/;
const MAX_COMPANY = 60;
/**
 * Letters, digits, spaces and the punctuation real company names use (L'Oréal, P&G, AT&T,
 * Wieden+Kennedy). Kept in step with the guide side's `COMPANY_RE` in `interview-params.ts`: a name
 * the interview accepted must survive the hand-off, or the panel falls back to a name rebuilt from
 * the guide slug (QA-683: "Wieden+Kennedy" arrived as "Wieden Kennedy").
 */
const COMPANY_RE = /^[\p{L}\p{N} .,&+'’()-]+$/u;

export interface InterviewArrivalParams {
  /** The company the interview was about, or null when the URL does not carry a usable one. */
  company: string | null;
  /** '' when the URL carries no usable address. */
  email: string;
  /** The guide page slug from `utm_source`, or '' when absent. */
  guideSource: string;
  /** The three rounds in order: the two skill rounds, then the story. */
  levels: [InterviewRoundLevel, InterviewRoundLevel, InterviewRoundLevel];
  pack: InterviewArrivalPack;
}

/** The URL parameters that carry a scorecard. A company that does not pass the check is left out. */
export function encodeInterviewResult(
  levels: readonly InterviewRoundLevel[],
  company: string | null | undefined,
): Record<string, string> {
  const params: Record<string, string> = {};
  if (levels.length === 3) {
    params[INTERVIEW_ARRIVAL_PARAMS.rounds] = levels.map((level) => LEVEL_CODE[level]).join('');
  }
  const name = company?.trim().slice(0, MAX_COMPANY);
  if (name && COMPANY_RE.test(name)) {
    params[INTERVIEW_ARRIVAL_PARAMS.company] = name;
  }
  return params;
}

function isPack(value: string): value is InterviewArrivalPack {
  return (INTERVIEW_PACK_KEYS as readonly string[]).includes(value);
}

/**
 * The interview arrival the URL describes, or null for every other sign-up load. Strict on what the
 * rail cannot render without (the campaign, a known pack, three round results); tolerant of a missing
 * or damaged company, which the rail then takes from the guide slug or simply leaves out.
 */
export function parseInterviewArrival(
  searchParams: Pick<URLSearchParams, 'get'> | null,
): InterviewArrivalParams | null {
  if (!searchParams || searchParams.get('utm_campaign') !== INTERVIEW_ARRIVAL_CAMPAIGN) {
    return null;
  }
  const pack = searchParams.get('utm_content')?.trim() ?? '';
  const rounds = searchParams.get(INTERVIEW_ARRIVAL_PARAMS.rounds)?.trim() ?? '';
  if (!(isPack(pack) && ROUNDS_RE.test(rounds))) {
    return null;
  }
  const levels = rounds.split('').map((code) => CODE_LEVEL[code]) as InterviewArrivalParams['levels'];

  const rawCompany = searchParams.get(INTERVIEW_ARRIVAL_PARAMS.company)?.trim().slice(0, MAX_COMPANY) ?? '';
  const company = rawCompany && COMPANY_RE.test(rawCompany) ? rawCompany : null;

  return {
    company,
    email: prefilledEmail(searchParams),
    guideSource: searchParams.get('utm_source')?.trim().slice(0, 120) ?? '',
    levels,
    pack,
  };
}
